import type { AggregatedStat, IStats, Targeting, TTDCampaign } from '@feathr/blackbox';
import type { ListResponse } from '@feathr/rachis';

import type { IAudienceBreakdownReturn } from './TargetAudienceOverview.useAudienceBreakdown';
import useAudienceBreakdown from './TargetAudienceOverview.useAudienceBreakdown';

export interface IProps {
  campaign: TTDCampaign;
  targetings: ListResponse<Targeting>;
  dailyStats: AggregatedStat;
}

export interface IAudienceStatsReturn
  extends Omit<IAudienceBreakdownReturn, 'numDevicesPerPerson' | 'numNewDevices'> {
  numReachedUsers: NonNullable<IStats['num_persons_reached']>['total'];
  numReachedEmails: NonNullable<IStats['num_persons_reached']>['email'];
  numReachedLookalike: NonNullable<IStats['num_persons_reached']>['lookalike'];
  numReachedCookies: NonNullable<IStats['num_persons_reached']>['device'];
  numReachedDevices: NonNullable<IStats['num_devices_reached']>['total_targetable'];
  numDevicesPerPerson: NonNullable<IStats['num_devices_reached']>['avg_per_person'];
  numNewDevices: NonNullable<IStats['num_devices_reached']>['new'];
}

export interface IUseAudienceStats extends IAudienceStatsReturn {
  context: 'report';
  isEmpty: boolean;
  isLoading: boolean;
}

function useAudienceStats({
  campaign,
  targetings,
  dailyStats,
}: IProps): Readonly<IUseAudienceStats> {
  const audienceData = useAudienceBreakdown({ campaign, targetings });

  const {
    numTargetedReachableUsers,
    numTargetedReachableEmails,
    targetedLookalikeAudienceSize,
    numTargetedReachableCookies,
    numTargetedReachableDevices,
    isLoading,
  } = audienceData;

  const devicesReached = dailyStats.get('num_devices_reached');
  const personsReached = dailyStats.get('num_persons_reached');

  const numReachedDevices = devicesReached?.total_targetable ?? 0;
  const numDevicesPerPerson = devicesReached?.avg_per_person ?? 0;
  const numNewDevices = devicesReached?.new ?? 0;

  const numReachedCookies = personsReached?.device ?? 0;
  const numReachedEmails = personsReached?.email ?? 0;
  const numReachedLookalike = personsReached?.lookalike ?? 0;
  const numReachedUsers = personsReached?.total ?? 0;

  const isEmpty = numReachedUsers === 0;

  return {
    // From Breakdown
    numTargetedReachableUsers,
    numTargetedReachableEmails,
    numTargetedReachableCookies,
    numTargetedReachableDevices,

    // From Breakdown: Value derived from the lookalike audience slider
    targetedLookalikeAudienceSize,

    // From Stats
    numReachedUsers,
    numReachedEmails,
    numReachedLookalike,
    numReachedCookies,
    numReachedDevices,
    numDevicesPerPerson,
    numNewDevices,

    // Other
    isEmpty,
    isLoading,
    context: 'report',
  };
}

export default useAudienceStats;
